/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/pages/SearchPage.jsx
// Search page - integrates search bar, filters, and results display
// CBETA Style UI Redesign: Search mode selector + Compact NEAR input + Dynamic conditions

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Typography, Box, Grid, Alert, AlertTitle } from '@mui/material';
import SearchBar from '../components/Search/SearchBar';
import SearchResults from '../components/Search/SearchResults';
import SourceFilter from '../components/Search/SourceFilter';
import TagFilter from '../components/Search/TagFilter';
import SearchHistory from '../components/Search/SearchHistory';
// NearSearchForm is deprecated - now using CompactNearInput integrated in SearchBar
// import NearSearchForm from '../components/Search/NearSearchForm';
import { advancedSearch } from '../services/searchService';
// Frontend snippet extraction (Plan B)
import { groupResultsWithMultipleSnippets } from '../utils/snippetExtractor';
import { createCondition, serializeConditionsForApi } from '../utils/queryBuilder';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Search state
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedSources, setSelectedSources] = useState([]); // Selected source codes
  const [selectedTags, setSelectedTags] = useState([]); // Selected tag IDs
  // Facets 三種 state（D4）：
  //   undefined → 未搜尋前 / loading / 部署縫隙 → Filter fallback 到全域 use_count
  //   {} 空物件 → 已搜尋但無任何匹配 → Filter 全 disabled
  //   { [id]: n } → 正常
  const [facets, setFacets] = useState(undefined);

  // CBETA Style: Search mode state ('basic' | 'advanced' | 'near')
  const [searchMode, setSearchMode] = useState('basic');

  // NEAR search state
  const [nearState, setNearState] = useState({
    keyword1: '',
    keyword2: '',
    distance: 10,
  });

  // Advanced search conditions
  const [advancedConditions, setAdvancedConditions] = useState([createCondition('AND')]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  // ref PC-6 (2026-05-07): parser graceful-drop warnings (phrase_dropped /
  //   near_dropped / not_dropped / minus_dropped / near_distance_bumped)
  const [warnings, setWarnings] = useState([]);

  // Track if URL params have been processed (avoid duplicate search)
  const initialSearchDone = useRef(false);

  /**
   * 執行搜尋
   */
  const performSearch = async (searchQuery, searchPage = 1) => {
    if (!searchQuery || !searchQuery.trim()) {
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      // Phase 1B: 進階模式才送結構化 conditions（per-condition field selector）；
      // basic / near 模式維持既有 query string path 行為等價，避免 conditions
      // path 與 basic/near cache key cross-pollution。
      const conditions =
        searchMode === 'advanced' ? serializeConditionsForApi(advancedConditions) : undefined;

      const response = await advancedSearch(searchQuery, {
        page: searchPage,
        pageSize,
        sortBy: 'relevance',
        enableCrossLanguage: true,
        sources: selectedSources, // 傳遞選中的來源代碼
        tags: selectedTags, // 傳遞選中的標籤 ID
        conditions, // Phase 1B：結構化條件（advanced 模式才有）
      });

      // Plan B：使用前端片段提取（CBETA 風格多片段顯示）
      const rawResults = response.data?.results || [];
      const groupedResults = groupResultsWithMultipleSnippets(rawResults, searchQuery, {
        maxSnippetsPerDocument: 5,
        contextLength: 50,
      });

      setResults(groupedResults);
      setTotal(response.data?.total || 0);
      // 後端回傳 facets 時套用；缺失時 fallback 到空物件（觸發 Filter「已搜尋但無匹配」行為）
      // 注意：這裡不 setFacets(undefined)，undefined 僅保留給「尚未搜尋」初始狀態
      setFacets(response.data?.facets || { tags: {}, sources: {} });
      // ref PC-6: parser warnings 顯示於 results 上方 Alert（非阻擋；Phase C 之前路徑無此 field 安全 fallback []）
      setWarnings(response.meta?.warnings || []);
      setPage(searchPage);
    } catch (err) {
      console.error('搜尋失敗:', err);
      setError(err.message || '搜尋失敗，請稍後再試');
      setResults([]);
      setTotal(0);
      // ref PC-11 (2026-05-08, Codex Round 1 C4 配套): error path 仍顯示 parser warnings
      //   searchService.js attach err.warnings (commit 4); 修前強制 setWarnings([])
      //   會吞掉 backend attach 的 parserWarnings (e.g. NEAR distance bump)
      setWarnings(err.warnings || []);
      // 錯誤時保留上次 facets（連續性，避免閃爍）— 不呼叫 setFacets
    } finally {
      setLoading(false);
    }
  };

  /**
   * 將 query + page 寫入 URL（page=1 時省略 page 參數，保持 URL 乾淨）
   */
  const updateUrlParams = (searchQuery, pageNum) => {
    const next = { q: searchQuery };
    if (pageNum > 1) {
      next.page = String(pageNum);
    }
    setSearchParams(next);
  };

  /**
   * 處理搜尋提交（從 SearchBar）
   */
  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
    setPage(1);
    updateUrlParams(searchQuery, 1);
    performSearch(searchQuery, 1);
  };

  /**
   * 初始化時檢查 URL 參數，自動執行搜尋（含 page 參數支援 deep linking）
   * 只在首次掛載時跑，避免 setSearchParams 觸發迴圈
   */
  useEffect(() => {
    if (initialSearchDone.current) return;
    // Mount 時直接標記已處理，避免後續 setSearchParams（由 handleSearch / handlePageChange 觸發）
    // 讓此 effect 重跑並二次呼叫 performSearch。
    initialSearchDone.current = true;

    const urlQuery = searchParams.get('q');
    if (!urlQuery) return;

    const rawPage = parseInt(searchParams.get('page'), 10);
    const initialPage = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;

    setQuery(urlQuery);
    performSearch(urlQuery, initialPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * 處理分頁變更（同步更新 URL，支援書籤、前進/後退、分享連結）
   */
  const handlePageChange = (newPage) => {
    updateUrlParams(query, newPage);
    performSearch(query, newPage);
  };

  /**
   * 當來源或標籤變更時，重新搜尋並重置至第 1 頁（同步 URL 移除 page 參數）
   */
  useEffect(() => {
    if (query && hasSearched) {
      updateUrlParams(query, 1);
      performSearch(query, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSources, selectedTags]);

  /**
   * 處理來源變更
   */
  const handleSourceChange = (newSources) => {
    setSelectedSources(newSources);
    setPage(1);
  };

  /**
   * 處理標籤變更
   */
  const handleTagChange = (newTags) => {
    setSelectedTags(newTags);
    setPage(1);
  };

  // SearchBar props（共用，避免重複）
  const searchBarProps = {
    onSearch: handleSearch,
    initialQuery: query,
    searchMode,
    onModeChange: setSearchMode,
    nearState,
    onNearStateChange: setNearState,
    advancedConditions,
    onAdvancedConditionsChange: setAdvancedConditions,
  };

  // 搜尋前：置中佈局
  if (!hasSearched) {
    return (
      <Box
        data-testid="landing-view"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 64px)',
          px: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, color: 'primary.dark', letterSpacing: 2, mb: 1 }}
        >
          搜尋佛法文獻
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          繁簡體自動互通，可用 + 組合多個關鍵字
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 860 }}>
          <SearchBar {...searchBarProps} />
          <SearchHistory onSelectQuery={handleSearch} limit={10} />
        </Box>
      </Box>
    );
  }

  // 搜尋後：吸頂搜尋欄 + 篩選器 + 結果
  return (
    <>
      {/* 吸頂搜尋欄 */}
      <Box
        data-testid="sticky-header"
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 50,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
          boxShadow: 1,
        }}
      >
        <Container maxWidth="xl">
          <SearchBar {...searchBarProps} />
        </Container>
      </Box>

      {/* 主內容區：左側篩選器 + 右側搜尋結果 */}
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <Grid container spacing={3}>
          {/* 左側篩選器 */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SourceFilter
                selectedSources={selectedSources}
                onSourceChange={handleSourceChange}
                searchFacets={facets?.sources}
              />
              <TagFilter
                selectedTags={selectedTags}
                onTagChange={handleTagChange}
                searchFacets={facets?.tags}
              />
            </Box>
          </Grid>

          {/* 右側搜尋結果 */}
          <Grid size={{ xs: 12, md: 9 }}>
            {/* ref PC-6: parser graceful-drop / NEAR distance bump warnings */}
            {warnings.length > 0 && (
              <Alert
                severity="warning"
                onClose={() => setWarnings([])}
                sx={{ mb: 2 }}
                data-testid="parser-warnings"
              >
                <AlertTitle>查詢提示</AlertTitle>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {warnings.map((w, i) => (
                    <li key={i}>{w.message}</li>
                  ))}
                </Box>
              </Alert>
            )}
            <SearchResults
              results={results}
              total={total}
              page={page}
              pageSize={pageSize}
              query={query}
              loading={loading}
              error={error}
              onPageChange={handlePageChange}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default SearchPage;
